/**
 * <b>묶음 탭</b>의 그림 (사용자 지시 2026-09-29 「아이콘 + 라벨」). 그림 자체는
 * `components/MenuIcon.tsx` 에 있고, 최상위 아홉 줄의 표는 `icons.ts` 에 따로 있다.
 *
 * <h2>왜 표를 둘로 두는가</h2>
 * 최상위 아홉은 <b>사이드바에 한꺼번에</b> 선다 — 그래서 아홉이 서로 달라야 하고, 하나라도 비면
 * 기본값(격자)으로 떨어져 옆줄과 같은 얼굴이 된다({@code icons.ts} 머리말). 탭은 그렇지 않다.
 * 한 화면에 서는 것은 <b>한 묶음의 자식뿐</b>이라, 같은 그림이 두 묶음에 걸쳐 쓰여도 둘이 나란히
 * 보이는 날이 없다 — 책(`book`)이 게시판의 「사내지침」과 보미기술집의 「공종별 기술집」에 함께
 * 붙어 있는 것이 그래서 괜찮다. 지켜야 할 것은 <b>형제끼리의 구별</b> 하나다.
 *
 * <h2>없으면 안 그린다</h2>
 * {@link iconNameOf} 와 달리 기본값이 <b>없다</b>. 적히지 않은 키는 {@code null} 이고 탭은 글자만
 * 남는다 — 격자로 채우면 그 줄은 「무엇도 아닌 그림」이 열 개 늘어선 자리가 되어, 글자를 읽는 데
 * 오히려 방해가 된다. 관리자가 메뉴를 새로 세우는 날 여기 한 줄을 더하면 그림이 붙는다.
 *
 * <h2>둘째 줄에는 붙이지 않는다</h2>
 * 셋째 단(주간업무 › 본사)은 그림 없이 글자만 선다. 두 줄이 같은 옷을 입으면 어느 줄이 어느 줄의
 * 아래인지가 <b>크기 차이로만</b> 남는데, 그것이 사용자가 지적한 「두 줄의 관계가 안 보인다」다.
 * 그림의 있고 없음은 크기보다 먼저 읽히는 차이다.
 */
const ICON_BY_TAB_KEY: Record<string, string> = {
  /*
    현장 현황 — 짓는 중(안전모) · 아직 딴 것이 아님(서류) · 다 지음(건물).

    <p>준공을 체크(`check`)로 두었다가 건물로 바꿨다(2026-09-29 시안). 그 그림은 <b>체크가 든
    서류판</b>이라, 바로 옆 입찰의 서류판과 15px 에서 같은 덩어리로 보였다 — 한 줄에 선 형제끼리
    갈리는 것이 이 표가 지켜야 할 하나다.
  */
  site_status_active: 'hardhat',
  site_status_bid: 'clipboard',
  site_status_completed: 'building',
  site_incentive: 'card',

  // 업무 일정 — 주간은 빈 달력, 월간은 줄이 찬 달력. 둘이 한 줄에 서므로 갈라 둔다.
  dashboard: 'chart',
  work_weekly: 'calendar',
  work_monthly: 'calendar-days',

  // 인사관리 — 여덟이 한 줄에 선다. 여기는 형제 구별이 가장 빡빡한 자리다.
  hr_placement: 'grid',
  hr_records: 'folder',
  hr_leave_status: 'sun',
  hr_attendance: 'clock',
  hr_pc_off: 'power',
  hr_doc_templates: 'file',
  hr_business_cards: 'card',
  hr_tech_education: 'book',

  // 자료실 — 나누고 · 찾고 · 올리고 · 시험한다.
  docs_categories: 'grid',
  docs_browse: 'search',
  docs_submissions: 'upload',
  docs_lab: 'flask',

  // 게시판
  board_thanks: 'shield',
  news_guidelines: 'book',
  news_employee: 'user',
  news_polls: 'check',

  // 보미기술집
  tech_cases: 'book',
  tech_new: 'star',

  // 자산관리
  asset_requests: 'calendar',
  asset_registry: 'box',

  /*
    환경설정 — 열넷이다. 사람(`users`)과 한 사람(`user`)이 한 줄에 함께 서지만, 15px 에서도
    둘(겹친 머리)과 하나(가운데 머리)는 갈린다. 여기서 더 쪼개면 새 그림이 서로 닮기 시작한다.
  */
  settings_departments: 'users',
  settings_sites: 'layout',
  vendor_master: 'building',
  settings_menus: 'drag',
  settings_users: 'user',
  settings_roles: 'lock',
  settings_permission_diagnosis: 'search',
  settings_backup: 'cloud',
  settings_mail: 'mail',
  settings_push: 'bell',
  settings_external_api: 'link',
  settings_shortcuts: 'star',
  settings_login: 'key',
  settings_homepage: 'link',
  settings_safety_admins: 'shield',
  settings_exit_survey: 'clipboard',
  settings_error_log: 'file',
  settings_release_notes: 'clipboard',
};

export function tabIconOf(menuKey: string): string | null {
  return ICON_BY_TAB_KEY[menuKey] ?? null;
}
